import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye,
  EyeOff, 
  Save, 
  Upload, 
  Calendar, 
  Tag, 
  Search,
  Settings,
  LogOut,
  User,
  Lock,
  Mail,
  ArrowLeft,
  ImageIcon,
  Bold,
  Italic,
  Link,
  List,
  AlignLeft,
  AlignCenter,
  Heading,
  Star,
  CheckCircle,
  XCircle
} from 'lucide-react';

  const { toast } = require("../hooks/use-toast");
export const BlogAdmin = () => {
  const [siteSettings, setSiteSettings] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, editor, settings
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Authentication states
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [resetForm, setResetForm] = useState({ email: '' });
  const [showReset, setShowReset] = useState(false);
  const [authError, setAuthError] = useState('');
  
  // Migration states
  const [migrationStatus, setMigrationStatus] = useState('');
  const [showMigration, setShowMigration] = useState(false);
  
  // Debug panel states
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    backendUrl: process.env.REACT_APP_BACKEND_URL || 'Not configured',
    timestamp: null,
    responseTime: null,
    httpStatus: null,
    requestDetails: null,
    errorDetails: null,
    networkInfo: {
      userAgent: navigator.userAgent,
      cookiesEnabled: navigator.cookieEnabled,
      language: navigator.language,
      platform: navigator.platform
    }
  });

  // Testimonials states
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    text: '',
    rating: 5,
    approved: false
  });

  // SMTP password visibility state
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Password change states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Post editor states
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'Douglas Mitchell, DPT',
    category: 'Physical Therapy',
    tags: [],
    image: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    status: 'draft', // draft, published, scheduled
    publishDate: new Date().toISOString().split('T')[0],
    publishTime: '09:00',
    readTime: '5 min read'
  });

  // Ref for content textarea
  const contentTextareaRef = useRef(null);

  // Initialize - check authentication and load posts
  useEffect(() => {
    checkAuthentication();
    if (isAuthenticated) {
      loadPosts();
      loadSiteSettings();
      loadTestimonials();
    }
  }, [isAuthenticated]);

  // Authentication functions
  const checkAuthentication = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/auth/check`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.log('Not authenticated');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    
    // Start debug timing
    const startTime = performance.now();
    const timestamp = new Date().toISOString();
    
    try {
      const loginUrl = `${process.env.REACT_APP_BACKEND_URL}/api/blog/auth/login`;
      
      // Update debug info with request details
      setDebugInfo(prev => ({
        ...prev,
        timestamp,
        requestDetails: {
          url: loginUrl,
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        },
        responseTime: null,
        httpStatus: null,
        errorDetails: null
      }));
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      });
      
      // Calculate response time
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);
      
      // Update debug info with response details
      setDebugInfo(prev => ({
        ...prev,
        responseTime: `${responseTime}ms`,
        httpStatus: response.status
      }));
      
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        setIsAuthenticated(true);
        setLoginForm({ username: '', password: '' });
        
        // Update debug info with success
        setDebugInfo(prev => ({
          ...prev,
          errorDetails: null
        }));
      } else {
        let msg = `HTTP ${response.status}`;
        let errorDetails = { status: response.status, statusText: response.statusText };
        
        try {
          const txt = await response.text();
          errorDetails.responseBody = txt;
          try {
            const obj = JSON.parse(txt);
            msg = obj.message || obj.detail || txt || msg;
            errorDetails.parsedResponse = obj;
          } catch (e) {
            msg = txt || msg;
          }
        } catch (e) {
          errorDetails.readError = e.message;
        }
        
        setAuthError(msg);
        setDebugInfo(prev => ({
          ...prev,
          errorDetails
        }));
      }
    } catch (error) {
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);
      
      setAuthError('Connection error. Please try again.');
      setDebugInfo(prev => ({
        ...prev,
        responseTime: `${responseTime}ms (failed)`,
        httpStatus: 'Network Error',
        errorDetails: {
          type: 'NetworkError',
          message: error.message,
          stack: error.stack
        }
      }));
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.log('Logout error:', error);
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setActiveView('dashboard');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resetForm)
      });
      
      if (response.ok) {
        toast({ title: 'Email sent', description: 'Password reset instructions have been sent to your email.' });
        setShowReset(false);
        setResetForm({ email: '' });
      } else {
        const error = await response.json();
        setAuthError(error.message || 'Reset failed');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password strength
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const error = await response.json();
        setPasswordError(error.message || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  // Blog management functions
  const loadPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const postsData = await response.json();
        
        // If no posts from database, load from static file
        if (!postsData || postsData.length === 0) {
          try {
            const { blogArticles } = await import('../data/blogArticles');
            setPosts(blogArticles || []);
          } catch (error) {
            console.error('Error importing static blog articles:', error);
            setPosts([]);
          }
        } else {
          setPosts(postsData);
        }
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      // Fallback to static file
      try {
        const { blogArticles } = await import('../data/blogArticles');
        setPosts(blogArticles || []);
      } catch (importError) {
        console.error('Error importing static blog articles:', importError);
        setPosts([]);
      }
    }
  };

  const loadSiteSettings = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSiteSettings(data);
      }
    } catch (e) { console.error('Load settings error', e); }
  };

  // ============ TESTIMONIALS FUNCTIONS ============
  
  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials?approved_only=false`, { 
        credentials: 'include' 
      });
      if (res.ok) {
        const data = await res.json();
        setTestimonials(data);
      }
    } catch (e) {
      console.error('Load testimonials error', e);
    } finally {
      setLoading(false);
    }
  };

  const saveTestimonial = async () => {
    try {
      setLoading(true);
      const url = currentTestimonial 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${currentTestimonial.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/testimonials`;
      
      const method = currentTestimonial ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(testimonialForm)
      });

      if (res.ok) {
        await loadTestimonials();
        setCurrentTestimonial(null);
        setTestimonialForm({ name: '', text: '', rating: 5, approved: false });
        setActiveView('testimonials');
      }
    } catch (e) {
      console.error('Save testimonial error', e);
    } finally {
      setLoading(false);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        await loadTestimonials();
      }
    } catch (e) {
      console.error('Delete testimonial error', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (testimonial) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${testimonial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ approved: !testimonial.approved })
      });

      if (res.ok) {
        await loadTestimonials();
      }
    } catch (e) {
      console.error('Toggle approval error', e);
    }
  };

  const editTestimonial = (testimonial) => {
    setCurrentTestimonial(testimonial);
    setTestimonialForm({
      name: testimonial.name,
      text: testimonial.text,
      rating: testimonial.rating,
      approved: testimonial.approved
    });
    setActiveView('testimonialEditor');
  };

  const newTestimonial = () => {
    setCurrentTestimonial(null);
    setTestimonialForm({ name: '', text: '', rating: 5, approved: false });
    setActiveView('testimonialEditor');
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handlePostChange = (field, value) => {
    setPostForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug from title
      if (field === 'title') {
        updated.slug = generateSlug(value);
        updated.metaTitle = value.length > 60 ? value.substring(0, 57) + '...' : value;
      }
      
      // Auto-generate meta description from excerpt
      if (field === 'excerpt') {
        updated.metaDescription = value.length > 160 ? value.substring(0, 157) + '...' : value;
      }
      
      return updated;
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file); // Changed from 'image' to 'file' to match backend
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        // Backend returns "/api/uploads/{filename}", we need full URL for display
        const fullImageUrl = result.imageUrl.startsWith('http') 
          ? result.imageUrl 
          : `${process.env.REACT_APP_BACKEND_URL}${result.imageUrl}`;
        setPostForm(prev => ({ ...prev, image: fullImageUrl }));
        toast({ title: 'Success', description: 'Image uploaded successfully!' });
      } else {
        toast({ title: 'Error', description: 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast({ title: 'Error', description: 'Upload failed. Please try again.' });
    }
    setLoading(false);
  };

  // Text formatting functions for editor
  const insertFormatting = (format) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = postForm.content.substring(start, end);
    const beforeText = postForm.content.substring(0, start);
    const afterText = postForm.content.substring(end);

    let formattedText = '';
    let cursorOffset = 0;

    switch (format) {
      case 'bold':
        formattedText = selectedText ? `<strong>${selectedText}</strong>` : '<strong></strong>';
        cursorOffset = selectedText ? 0 : 8; // Position cursor inside tags
        break;
      case 'italic':
        formattedText = selectedText ? `<em>${selectedText}</em>` : '<em></em>';
        cursorOffset = selectedText ? 0 : 4;
        break;
      case 'heading':
        formattedText = selectedText ? `<h2>${selectedText}</h2>` : '<h2></h2>';
        cursorOffset = selectedText ? 0 : 4;
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          formattedText = selectedText ? `<a href="${url}">${selectedText}</a>` : `<a href="${url}">Link Text</a>`;
        } else {
          return;
        }
        break;
      case 'list':
        formattedText = selectedText 
          ? `<ul>\n  <li>${selectedText}</li>\n</ul>` 
          : '<ul>\n  <li></li>\n</ul>';
        cursorOffset = selectedText ? 0 : 11;
        break;
      case 'paragraph':
        formattedText = selectedText ? `<p>${selectedText}</p>` : '<p></p>';
        cursorOffset = selectedText ? 0 : 3;
        break;
      case 'image':
        const imgUrl = prompt('Enter image URL:');
        if (imgUrl) {
          const altText = prompt('Enter alt text (optional):') || 'Image';
          formattedText = `<img src="${imgUrl}" alt="${altText}" />`;
        } else {
          return;
        }
        break;
      default:
        return;
    }

    const newContent = beforeText + formattedText + afterText;
    handlePostChange('content', newContent);

    // Set cursor position after state update
    setTimeout(() => {
      if (textarea) {
        const newPosition = start + formattedText.length - cursorOffset;
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  // Content migration function
  const handleContentMigration = async () => {
    setMigrationStatus('Starting migration...');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/migrate-content`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setMigrationStatus(result.message || 'Migration completed successfully!');
        toast({ 
          title: 'Migration Complete', 
          description: 'Content has been migrated to the database.' 
        });
        loadPosts(); // Reload posts to show migrated content
      } else {
        const error = await response.json();
        setMigrationStatus(`Migration failed: ${error.message || error.detail || 'Unknown error'}`);
        toast({ title: 'Migration Failed', description: 'Could not migrate content.' });
      }
    } catch (error) {
      setMigrationStatus(`Migration error: ${error.message}`);
      toast({ title: 'Error', description: 'Migration request failed.' });
    }
    setLoading(false);
  };

  const savePost = async () => {
    if (!postForm.title) {
      toast({ title: 'Error', description: 'Post title is required.' });
      return;
    }

    // Auto-generate slug if empty
    if (!postForm.slug) {
      const slug = postForm.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setPostForm(prev => ({ ...prev, slug }));
    }

    setLoading(true);
    
    try {
      const url = currentPost 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${currentPost.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`;
      
      const method = currentPost ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(postForm)
      });
      
      if (response.ok) {
        toast({ 
          title: 'Success', 
          description: currentPost ? 'Post updated successfully!' : 'Post created successfully!' 
        });
        loadPosts();
        setActiveView('dashboard');
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.message || 'Failed to save post' });
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: 'Error', description: 'Connection error. Please try again.' });
    }
    setLoading(false);
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Post deleted successfully!' });
        loadPosts();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.message || 'Failed to delete post' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: 'Error', description: 'Connection error. Please try again.' });
    }
  };

  const editPost = (post) => {
    setCurrentPost(post);
    
    // Normalize image URL - convert relative paths to absolute URLs
    let imageUrl = post.image || '';
    if (imageUrl && !imageUrl.startsWith('http')) {
      // If image starts with /api, prepend backend URL
      imageUrl = imageUrl.startsWith('/') 
        ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
        : imageUrl;
    }
    
    setPostForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author: post.author || 'Douglas Mitchell, DPT',
      category: post.category || 'Physical Therapy',
      tags: post.tags || [],
      image: imageUrl,
      metaTitle: post.metaTitle || post.meta_title || '',
      metaDescription: post.metaDescription || post.meta_description || '',
      metaKeywords: post.metaKeywords || post.meta_keywords || '',
      status: post.status || 'draft',
      publishDate: post.date ? post.date.split('T')[0] : new Date().toISOString().split('T')[0],
      publishTime: '09:00',
      readTime: post.readTime || post.read_time || '5 min read'
    });
    setActiveView('editor');
  };

  const resetPostForm = () => {
    setPostForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: 'Douglas Mitchell, DPT',
      category: 'Physical Therapy',
      tags: [],
      image: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      status: 'draft',
      publishDate: new Date().toISOString().split('T')[0],
      publishTime: '09:00',
      readTime: '5 min read'
    });
  };

  const newPost = () => {
    setCurrentPost(null);
    resetPostForm();
    setActiveView('editor');
  };

  // Filter posts based on search
  const filteredPosts = posts.filter(post => 
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit3 size={32} className="text-black" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2"><span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Blog Admin</h1>
              <p className="text-gray-400">Sign in to manage your blog content</p>
            </div>

            {!showReset ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <User size={16} className="inline mr-2" />
                    Username
                  </label>
                  <input
                    type="text"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Lock size={16} className="inline mr-2" />
                    Password
                  </label>
                  <input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    required
                  />
                </div>

                {authError && (
                  <>
                    {/* Visible backend response debug for troubleshooting */}
                    <div className="text-xs text-gray-400 -mt-2">
                      <span>Endpoint: {process.env.REACT_APP_BACKEND_URL}/api/blog/auth/login</span>
                    </div>
                    <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
                      {authError}
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>

                <button
                  type="button"
                  onClick={() => setShowReset(true)}
                  className="w-full text-yellow-400 hover:text-yellow-300 text-sm transition-colors"
                >
                  Forgot your password?
                </button>
                
                {/* Debug Panel Toggle */}
                <div className="border-t border-gray-700 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDebugPanel(!showDebugPanel)}
                    className="w-full text-xs text-gray-500 hover:text-gray-400 transition-colors"
                  >
                    {showDebugPanel ? '▼ Hide Debug Info' : '▶ Show Debug Info'}
                  </button>
                  
                  {showDebugPanel && (
                    <div className="mt-3 p-3 bg-gray-900 border border-gray-600 rounded-lg text-xs">
                      <h4 className="text-gray-300 font-semibold mb-2">Technical Debug Information</h4>
                      
                      <div className="space-y-2 text-gray-400">
                        <div>
                          <span className="text-yellow-400">Backend URL:</span> {debugInfo.backendUrl}
                        </div>
                        
                        {debugInfo.timestamp && (
                          <div>
                            <span className="text-yellow-400">Last Request:</span> {new Date(debugInfo.timestamp).toLocaleString()}
                          </div>
                        )}
                        
                        {debugInfo.responseTime && (
                          <div>
                            <span className="text-yellow-400">Response Time:</span> {debugInfo.responseTime}
                          </div>
                        )}
                        
                        {debugInfo.httpStatus && (
                          <div>
                            <span className="text-yellow-400">HTTP Status:</span> {debugInfo.httpStatus}
                          </div>
                        )}
                        
                        {debugInfo.requestDetails && (
                          <div>
                            <div className="text-yellow-400">Request Details:</div>
                            <div className="ml-2 font-mono text-xs">
                              <div>URL: {debugInfo.requestDetails.url}</div>
                              <div>Method: {debugInfo.requestDetails.method}</div>
                              <div>Credentials: {debugInfo.requestDetails.credentials}</div>
                            </div>
                          </div>
                        )}
                        
                        {debugInfo.errorDetails && (
                          <div>
                            <div className="text-red-400">Error Details:</div>
                            <div className="ml-2 font-mono text-xs">
                              {debugInfo.errorDetails.type && <div>Type: {debugInfo.errorDetails.type}</div>}
                              {debugInfo.errorDetails.message && <div>Message: {debugInfo.errorDetails.message}</div>}
                              {debugInfo.errorDetails.status && <div>Status: {debugInfo.errorDetails.status}</div>}
                              {debugInfo.errorDetails.statusText && <div>Status Text: {debugInfo.errorDetails.statusText}</div>}
                              {debugInfo.errorDetails.responseBody && (
                                <div>Response: {debugInfo.errorDetails.responseBody.substring(0, 200)}...</div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <div className="text-yellow-400">Environment:</div>
                          <div className="ml-2 font-mono text-xs">
                            <div>User Agent: {debugInfo.networkInfo.userAgent.substring(0, 60)}...</div>
                            <div>Cookies: {debugInfo.networkInfo.cookiesEnabled ? 'Enabled' : 'Disabled'}</div>
                            <div>Language: {debugInfo.networkInfo.language}</div>
                            <div>Platform: {debugInfo.networkInfo.platform}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail size={16} className="inline mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetForm.email}
                    onChange={(e) => setResetForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    required
                  />
                </div>

                {authError && (
                  <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
                    {authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Instructions'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowReset(false);
                    setAuthError('');
                  }}
                  className="w-full text-yellow-400 hover:text-yellow-300 text-sm transition-colors"
                >
                  Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Interface
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
              <Edit3 size={20} className="text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Blog Admin</h1>
              <p className="text-sm text-gray-400"><span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Content Management</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-300">Welcome, {currentUser?.username}</span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-64 bg-gray-800 border-r border-gray-700 min-h-screen p-6">
          <div className="space-y-2">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'dashboard' ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Settings size={20} />
              <span>Dashboard</span>
            </button>
            
            <button
              onClick={newPost}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'editor' && !currentPost ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Plus size={20} />
              <span>New Post</span>
            </button>

            <button
              onClick={() => setActiveView('settings')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'settings' ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <User size={20} />
              <span>Account Settings</span>
            </button>
            <button
              onClick={() => setActiveView('siteSettings')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'siteSettings' ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Settings size={20} />
              <span>Site Settings</span>
            </button>

            <button
              onClick={() => setActiveView('testimonials')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'testimonials' ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Star size={20} />
              <span>Testimonials</span>
            </button>
            
            <button
              onClick={() => setShowMigration(true)}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-blue-300 hover:bg-gray-700"
            >
              <ArrowLeft size={20} />
              <span>Migrate Content</span>
            </button>
          </div>
          
          <div className="mt-8">
            <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="text-center p-3 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-yellow-500">{posts.length}</div>
                <div className="text-xs text-gray-400">Total Posts</div>
              </div>
              <div className="text-center p-3 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-green-500">
                  {posts.filter(p => p.status === 'published').length}
                </div>
                <div className="text-xs text-gray-400">Published</div>
              </div>
              <div className="text-center p-3 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-orange-500">
                  {posts.filter(p => p.status === 'draft').length}
                </div>
                <div className="text-xs text-gray-400">Drafts</div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {activeView === 'dashboard' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Blog Posts</h2>
                <button
                  onClick={newPost}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-3 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Plus size={20} />
                  <span>New Post</span>
                </button>
              </div>

              {/* Search */}
              <div className="mb-6 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                />
              </div>

              {/* Posts List */}
              <div className="grid gap-6">
                {filteredPosts.map((post, index) => (
                  <div key={index} className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-semibold text-white">{post.title}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            post.status === 'published' 
                              ? 'bg-green-900 text-green-300 border border-green-700'
                              : post.status === 'scheduled'
                              ? 'bg-blue-900 text-blue-300 border border-blue-700'
                              : 'bg-orange-900 text-orange-300 border border-orange-700'
                          }`}>
                            {post.status}
                          </span>
                        </div>
                        
                        <p className="text-gray-400 mb-4 line-clamp-2">{post.excerpt}</p>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <span>By {post.author}</span>
                          <span>{post.date}</span>
                          <span>{post.category}</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      
                      {post.image && (
                        <div className="ml-6">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-24 h-16 object-cover rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
                      <div className="flex space-x-2">
                        {post.tags?.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => editPost(post)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Edit Post"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Preview Post"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => deletePost(post.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Delete Post"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Edit3 size={32} className="text-gray-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-400 mb-2">No posts found</h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first blog post'}
                  </p>
                  <button
                    onClick={newPost}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-3 rounded-lg transition-colors"
                  >
                    Create New Post
                  </button>
                </div>
              )}
            </div>
          )}

          {activeView === 'editor' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setActiveView('dashboard')}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="text-2xl font-bold text-white">
                    {currentPost ? 'Edit Post' : 'New Post'}
                  </h2>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setPostForm(prev => ({ ...prev, status: 'draft' }))}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      postForm.status === 'draft'
                        ? 'bg-orange-500 text-black'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Save as Draft
                  </button>
                  <button
                    onClick={() => setPostForm(prev => ({ ...prev, status: 'published' }))}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      postForm.status === 'published'
                        ? 'bg-green-500 text-black'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Publish
                  </button>
                  <button
                    onClick={savePost}
                    disabled={loading || !postForm.title}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    <Save size={16} />
                    <span>{loading ? 'Saving...' : 'Save Post'}</span>
                  </button>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Editor */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                    <input
                      type="text"
                      value={postForm.title || ''}
                      onChange={(e) => handlePostChange('title', e.target.value)}
                      placeholder="Enter your post title..."
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-lg focus:outline-none focus:border-yellow-500"
                    />
                  </div>

                  {/* Slug */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">URL Slug</label>
                    <input
                      type="text"
                      value={postForm.slug || ''}
                      onChange={(e) => handlePostChange('slug', e.target.value)}
                      placeholder="url-friendly-post-name"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      URL: gymratrehab.com/blog/{postForm.slug || 'post-slug'}
                    </p>
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
                    <textarea
                      value={postForm.excerpt || ''}
                      onChange={(e) => handlePostChange('excerpt', e.target.value)}
                      placeholder="Brief description of your post..."
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    />
                  </div>

                  {/* Content Editor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
                    <div className="border border-gray-700 rounded-lg overflow-hidden">
                      {/* Toolbar */}
                      <div className="bg-gray-700 px-4 py-2 border-b border-gray-600 flex items-center space-x-2">
                        <button 
                          type="button"
                          onClick={() => insertFormatting('bold')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Bold (Ctrl+B)"
                        >
                          <Bold size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('italic')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Italic (Ctrl+I)"
                        >
                          <Italic size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('heading')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Heading 2"
                        >
                          <Heading size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('link')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Insert Link"
                        >
                          <Link size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('list')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Bulleted List"
                        >
                          <List size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('paragraph')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Paragraph"
                        >
                          <AlignLeft size={16} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => insertFormatting('image')}
                          className="p-2 text-gray-300 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          title="Insert Image"
                        >
                          <ImageIcon size={16} />
                        </button>
                      </div>
                      
                      {/* Content Area */}
                      <textarea
                        ref={contentTextareaRef}
                        value={postForm.content || ''}
                        onChange={(e) => handlePostChange('content', e.target.value)}
                        placeholder="Write your blog post content here... You can use HTML tags for formatting."
                        rows={20}
                        className="w-full px-4 py-3 bg-gray-800 text-white focus:outline-none resize-none"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      You can use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a&gt;, &lt;ul&gt;, &lt;li&gt; for formatting.
                    </p>
                  </div>
                </div>

                {/* Sidebar Settings */}
                <div className="space-y-6">
                  {/* Featured Image */}
                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Featured Image</h3>
                    
                    {postForm.image ? (
                      <div className="space-y-4">
                        <img
                          src={postForm.image}
                          alt="Featured"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={postForm.image || ''}
                            onChange={(e) => handlePostChange('image', e.target.value)}
                            placeholder="Image URL"
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                          />
                          <button
                            onClick={() => handlePostChange('image', '')}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="w-full h-32 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors"
                        >
                          <div className="text-center">
                            <Upload size={24} className="text-gray-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Click to upload image</p>
                          </div>
                        </label>
                        <div className="mt-3">
                          <input
                            type="text"
                            value={postForm.image || ''}
                            onChange={(e) => handlePostChange('image', e.target.value)}
                            placeholder="Or paste image URL"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Post Settings */}
                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Post Settings</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Author</label>
                        <input
                          type="text"
                          value={postForm.author || 'Douglas Mitchell, DPT'}
                          onChange={(e) => handlePostChange('author', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                        <select
                          value={postForm.category || 'Physical Therapy'}
                          onChange={(e) => handlePostChange('category', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        >
                          <option value="Physical Therapy">Physical Therapy</option>
                          <option value="Sports Injuries">Sports Injuries</option>
                          <option value="Recovery">Recovery</option>
                          <option value="Movement">Movement</option>
                          <option value="Health Tips">Health Tips</option>
                          <option value="Exercise">Exercise</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Reading Time</label>
                        <input
                          type="text"
                          value={postForm.readTime || '5 min read'}
                          onChange={(e) => handlePostChange('readTime', e.target.value)}
                          placeholder="5 min read"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Publish Date</label>
                        <input
                          type="date"
                          value={postForm.publishDate || new Date().toISOString().split('T')[0]}
                          onChange={(e) => handlePostChange('publishDate', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Publish Time</label>
                        <input
                          type="time"
                          value={postForm.publishTime || '09:00'}
                          onChange={(e) => handlePostChange('publishTime', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* SEO Settings */}
                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">SEO Settings</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Meta Title ({(postForm.metaTitle || '').length}/60)
                        </label>
                        <input
                          type="text"
                          value={postForm.metaTitle || ''}
                          onChange={(e) => handlePostChange('metaTitle', e.target.value)}
                          maxLength={60}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Meta Description ({(postForm.metaDescription || '').length}/160)
                        </label>
                        <textarea
                          value={postForm.metaDescription || ''}
                          onChange={(e) => handlePostChange('metaDescription', e.target.value)}
                          maxLength={160}
                          rows={3}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Keywords</label>
                        <input
                          type="text"
                          value={postForm.metaKeywords || ''}
                          onChange={(e) => handlePostChange('metaKeywords', e.target.value)}
                          placeholder="keyword1, keyword2, keyword3"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-500 text-sm mb-2">{postForm.title || 'Post Title'}</h4>
                      <p className="text-gray-300 text-xs line-clamp-3">{postForm.excerpt || 'Post excerpt...'}</p>
                      <div className="flex items-center space-x-2 mt-3 text-xs text-gray-500">
                        <span>{postForm.author || 'Author'}</span>
                        <span>•</span>
                        <span>{postForm.publishDate || 'Date'}</span>
                        <span>•</span>
                        <span>{postForm.readTime || '5 min read'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'settings' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Account Settings</h2>
              </div>

              <div className="max-w-2xl">
                {/* User Info */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-white mb-6">Account Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                      <input
                        type="text"
                        value={currentUser?.username || ''}
                        disabled
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <input
                        type="email"
                        value={currentUser?.email || ''}
                        disabled
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Change Password */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-6">Change Password</h3>
                  
                  <form onSubmit={handlePasswordChange} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Lock size={16} className="inline mr-2" />
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Lock size={16} className="inline mr-2" />
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                        required
                        minLength={8}
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Lock size={16} className="inline mr-2" />
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                        required
                        minLength={8}
                      />
                    </div>

                    {passwordError && (
                      <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
                        {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="bg-green-900/50 border border-green-700 rounded-lg p-3 text-green-200 text-sm">
                        {passwordSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Changing Password...' : 'Change Password'}
                    </button>
                  </form>

                  <div className="mt-8 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <h4 className="font-semibold text-blue-200 mb-2">Security Tips:</h4>
                    <ul className="text-sm text-blue-300 space-y-1">
                      <li>• Use a strong, unique password</li>
                      <li>• Include uppercase, lowercase, numbers, and symbols</li>
                      <li>• Don't reuse passwords from other accounts</li>
                      <li>• Change your password regularly</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'siteSettings' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Site Settings</h2>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(siteSettings)
                      });
                      if (response.ok) toast({ title: 'Settings saved', description: 'Site settings updated successfully.' });
                    } catch (e) { console.error('Save settings error', e); }
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg"
                >
                  Save Settings
                </button>
              </div>
              
              {/* Inline Site Settings Form - No external component to avoid re-render issues */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Business Name</label>
                  <input
                    type="text"
                    value={siteSettings?.business_name || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, business_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tagline</label>
                  <input
                    type="text"
                    value={siteSettings?.tagline || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, tagline: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  <input
                    type="text"
                    value={siteSettings?.phone || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={siteSettings?.email || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Hours</label>
                  <input
                    type="text"
                    value={siteSettings?.hours || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, hours: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Location Blurb</label>
                  <input
                    type="text"
                    value={siteSettings?.location_blurb || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, location_blurb: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Facebook URL</label>
                  <input
                    type="url"
                    value={siteSettings?.social_facebook || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, social_facebook: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Instagram URL</label>
                  <input
                    type="url"
                    value={siteSettings?.social_instagram || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, social_instagram: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">SEO Default Title</label>
                  <input
                    type="text"
                    value={siteSettings?.seo_default_title || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, seo_default_title: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">SEO Default Description</label>
                  <input
                    type="text"
                    value={siteSettings?.seo_default_description || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, seo_default_description: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">SEO Social Image URL</label>
                  <input
                    type="url"
                    value={siteSettings?.seo_social_image || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, seo_social_image: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pricing Initial</label>
                  <input
                    type="number"
                    value={siteSettings?.pricing_initial || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, pricing_initial: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pricing Follow-up</label>
                  <input
                    type="number"
                    value={siteSettings?.pricing_followup || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, pricing_followup: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Partner Discount</label>
                  <input
                    type="number"
                    value={siteSettings?.pricing_partner_discount || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, pricing_partner_discount: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>

                {/* SMTP Settings */}
                <div className="md:col-span-2 p-4 border border-gray-700 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">SMTP Settings (Contact Form Email)</h4>
                  <p className="text-sm text-gray-400 mb-2">Configure email settings to receive contact form submissions</p>
                  <div className="text-xs text-gray-500 mb-4 p-3 bg-gray-800 rounded border border-gray-700">
                    <p className="font-semibold text-yellow-500 mb-2">Common Provider Settings:</p>
                    <p className="mb-1"><strong>Gmail:</strong> smtp.gmail.com | Port: 587 | Security: TLS | <span className="text-yellow-400">Use App Password!</span></p>
                    <p className="mb-1"><strong>Outlook:</strong> smtp.office365.com | Port: 587 | Security: TLS</p>
                    <p><strong>GoDaddy:</strong> smtpout.secureserver.net | Port: 465 | Security: SSL</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Host</label>
                      <input
                        type="text"
                        placeholder="smtp.gmail.com or smtp.office365.com"
                        value={siteSettings?.smtp?.host || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, host: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Port</label>
                      <input
                        type="number"
                        placeholder="587 (Gmail/Outlook) or 465 (GoDaddy)"
                        value={siteSettings?.smtp?.port || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, port: parseInt(e.target.value) || 587 } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Security</label>
                      <select
                        value={siteSettings?.smtp?.security || 'TLS'}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, security: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      >
                        <option value="">Select Security Type</option>
                        <option value="TLS">TLS (Gmail, Outlook - Port 587)</option>
                        <option value="SSL">SSL (GoDaddy - Port 465)</option>
                        <option value="STARTTLS">STARTTLS</option>
                        <option value="NONE">NONE (Not Recommended)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username (Usually your email)</label>
                      <input
                        type="text"
                        placeholder="Info@gymratrehab.com"
                        value={siteSettings?.smtp?.username || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, username: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Password (Gmail: Use App Password!)</label>
                      <div className="relative">
                        <input
                          type={showSmtpPassword ? "text" : "password"}
                          placeholder="••••••••••••••••"
                          value={siteSettings?.smtp?.password || ''}
                          onChange={(e) => setSiteSettings(prev => ({ 
                            ...prev, 
                            smtp: { ...prev?.smtp, password: e.target.value } 
                          }))}
                          className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-300 transition-colors"
                          title={showSmtpPassword ? "Hide password" : "Show password"}
                        >
                          {showSmtpPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      <p className="text-xs text-yellow-400 mt-1">Gmail requires App Password (not regular password)</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">From Email</label>
                      <input
                        type="email"
                        placeholder="Info@gymratrehab.com"
                        value={siteSettings?.smtp?.from_email || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, from_email: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">From Name</label>
                      <input
                        type="text"
                        placeholder="GymRatRehab Contact Form"
                        value={siteSettings?.smtp?.from_name || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, from_name: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    
                    <div className="md:col-span-3 p-4 border-2 border-yellow-500 rounded-lg bg-yellow-500/10">
                      <label className="block text-sm font-medium text-yellow-400 mb-2">
                        ⚠️ REQUIRED: Send Contact Forms To (Recipient Email)
                      </label>
                      <input
                        type="email"
                        placeholder="Info@gymratrehab.com - REQUIRED!"
                        value={siteSettings?.smtp?.to_email || ''}
                        onChange={(e) => setSiteSettings(prev => ({ 
                          ...prev, 
                          smtp: { ...prev?.smtp, to_email: e.target.value } 
                        }))}
                        className="w-full px-3 py-2 bg-gray-700 border-2 border-yellow-500 rounded text-white focus:outline-none focus:border-yellow-400"
                      />
                      <p className="text-xs text-yellow-300 mt-1 font-semibold">⚠️ This field is REQUIRED! Contact form will not work without it.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Testimonials List View */}
          {activeView === 'testimonials' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Testimonials Management</h2>
                <button
                  onClick={newTestimonial}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg flex items-center space-x-2"
                >
                  <Plus size={20} />
                  <span>New Testimonial</span>
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {testimonials.length === 0 ? (
                    <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
                      <Star size={48} className="mx-auto text-gray-600 mb-4" />
                      <p className="text-gray-400">No testimonials yet. Click "New Testimonial" to add one.</p>
                    </div>
                  ) : (
                    testimonials.map((testimonial) => (
                      <div key={testimonial.id} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-white">{testimonial.name}</h3>
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    size={16}
                                    className={i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}
                                  />
                                ))}
                              </div>
                              {testimonial.approved ? (
                                <span className="px-2 py-1 text-xs font-semibold bg-green-500/20 text-green-400 rounded">
                                  Approved
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold bg-yellow-500/20 text-yellow-400 rounded">
                                  Pending
                                </span>
                              )}
                            </div>
                            <p className="text-gray-300 mb-3">{testimonial.text}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(testimonial.created_at).toLocaleDateString()} • Source: {testimonial.source}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => toggleApproval(testimonial)}
                              className={`p-2 rounded-lg transition-colors ${
                                testimonial.approved 
                                  ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                                  : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                              }`}
                              title={testimonial.approved ? 'Unapprove' : 'Approve'}
                            >
                              {testimonial.approved ? <XCircle size={20} /> : <CheckCircle size={20} />}
                            </button>
                            <button
                              onClick={() => editTestimonial(testimonial)}
                              className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={20} />
                            </button>
                            <button
                              onClick={() => deleteTestimonial(testimonial.id)}
                              className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Testimonial Editor View */}
          {activeView === 'testimonialEditor' && (
            <div>
              <div className="flex items-center space-x-4 mb-8">
                <button
                  onClick={() => setActiveView('testimonials')}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft size={24} className="text-gray-400" />
                </button>
                <h2 className="text-2xl font-bold text-white">
                  {currentTestimonial ? 'Edit Testimonial' : 'New Testimonial'}
                </h2>
              </div>

              <div className="max-w-3xl space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={testimonialForm.name}
                    onChange={(e) => setTestimonialForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Customer Name"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Testimonial Text *</label>
                  <textarea
                    value={testimonialForm.text}
                    onChange={(e) => setTestimonialForm(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="What did the customer say about their experience?"
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Rating</label>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setTestimonialForm(prev => ({ ...prev, rating }))}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <Star
                          size={32}
                          className={rating <= testimonialForm.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}
                        />
                      </button>
                    ))}
                    <span className="ml-4 text-gray-400">{testimonialForm.rating} star{testimonialForm.rating !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="approved"
                    checked={testimonialForm.approved}
                    onChange={(e) => setTestimonialForm(prev => ({ ...prev, approved: e.target.checked }))}
                    className="w-5 h-5 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                  />
                  <label htmlFor="approved" className="text-sm font-medium text-gray-300">
                    Approve and show publicly
                  </label>
                </div>

                <div className="flex space-x-4 pt-6 border-t border-gray-700">
                  <button
                    onClick={saveTestimonial}
                    disabled={loading || !testimonialForm.name || !testimonialForm.text}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Save size={20} />
                    <span>{loading ? 'Saving...' : 'Save Testimonial'}</span>
                  </button>
                  <button
                    onClick={() => setActiveView('testimonials')}
                    disabled={loading}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      
      {/* Migration Modal */}
      {showMigration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Content Migration</h3>
            <p className="text-gray-300 mb-6">
              This will migrate existing blog posts from the file system to the database. 
              This is useful for moving from the old file-based blog system to the new database-driven system.
            </p>
            
            {migrationStatus && (
              <div className="mb-4 p-3 bg-gray-700 border border-gray-600 rounded-lg">
                <p className="text-sm text-gray-200">{migrationStatus}</p>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={handleContentMigration}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <ArrowLeft size={16} />
                <span>{loading ? 'Migrating...' : 'Start Migration'}</span>
              </button>
              <button
                onClick={() => {
                  setShowMigration(false);
                  setMigrationStatus('');
                }}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
